import { useEffect, useState } from 'react'
import useSWR from 'swr'
import type { LibraryCollectionResourcesPage, LibraryItemKind } from '@digital-twins/shared-types'

import { libraryCollectionsApi } from '@/lib/api'
import { PICKER_RESOURCE_KINDS, PICKER_SEARCH_DEBOUNCE_MS } from './collectionPickerConstants'
import { interleaveCollectionCandidates } from './collectionSelection'

function useCandidateKind(kind: LibraryItemKind, query: string, enabled: boolean) {
  return useSWR<LibraryCollectionResourcesPage>(
    enabled ? ['library/collection-create-candidates', kind, query] : null,
    () => libraryCollectionsApi.listCandidates({ kind, q: query }),
    { revalidateOnFocus: false, shouldRetryOnError: false, keepPreviousData: false },
  )
}

export interface UseCollectionCandidatesOptions {
  query: string
  socialEnabled: boolean
  enabled?: boolean
}

/** Create-only first-page suggestions. The caller mounts this only on Contents. */
export function useCollectionCandidates({ query, socialEnabled, enabled = true }: UseCollectionCandidatesOptions) {
  const currentQuery = query.trim()
  const [debouncedQuery, setDebouncedQuery] = useState('')
  useEffect(() => {
    if (!enabled || !currentQuery) {
      setDebouncedQuery('')
      return
    }
    const timer = setTimeout(() => setDebouncedQuery(currentQuery), PICKER_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [currentQuery, enabled])

  const active = enabled && Boolean(currentQuery) && debouncedQuery === currentQuery
  const study = useCandidateKind('study', currentQuery, active)
  const audience = useCandidateKind('audience', currentQuery, active)
  const dataset = useCandidateKind('dataset', currentQuery, active)
  const document = useCandidateKind('document', currentQuery, active)
  const topic = useCandidateKind('topic', currentQuery, active && socialEnabled)
  const byKind = { study, audience, dataset, document, topic }
  const kinds: Array<LibraryItemKind> = [...PICKER_RESOURCE_KINDS, ...(socialEnabled ? ['topic' as const] : [])]
  const failedKinds = active ? kinds.filter(kind => Boolean(byKind[kind].error)) : []
  const items = active ? interleaveCollectionCandidates(kinds.map(kind => byKind[kind].data?.items ?? [])) : []
  const hasQuery = enabled && Boolean(currentQuery)
  const isLoading = hasQuery && (!active || kinds.some(kind => byKind[kind].isLoading || byKind[kind].isValidating))

  return {
    items,
    hasQuery,
    isLoading,
    hasError: failedKinds.length > 0,
    failedKinds,
    retry: async () => { await Promise.allSettled(failedKinds.map(kind => byKind[kind].mutate())) },
  }
}
